import { IAssociatedPriceList } from '../../models/Entities/PriceList/IAssociatedPriceList';
import { PriceDetails } from '../../models/Entities/PriceList/PriceDetail';
import { AvailableMedicalService } from '../../models/Entities/MedicalServices/AvailableMedicalService';
import { StatusTone } from '../../theme/guideTokens';

/*
 * Lógica pura compartida por la pantalla de listas de precios (proveedor, tenant y convenios):
 * estado de vigencia, enriquecimiento y filtro de precios, formato de importes.
 */

export type PriceListStatusKey = 'active' | 'pending' | 'expired' | 'noValidity';

export interface PriceListStatus {
    key: PriceListStatusKey;
    /** Clave de traducción (`priceListInfo.statusValues.*`). */
    i18nKey: string;
    /** Tono de la etiqueta de estado de la guía. */
    tone: StatusTone;
    /** Símbolo para alto contraste (el estado nunca depende sólo del color). */
    symbol: string;
}

const STATUS: Record<PriceListStatusKey, PriceListStatus> = {
    active: { key: 'active', i18nKey: 'priceListInfo.statusValues.active', tone: 'success', symbol: '●' },
    pending: { key: 'pending', i18nKey: 'priceListInfo.statusValues.pending', tone: 'warning', symbol: '!' },
    expired: { key: 'expired', i18nKey: 'priceListInfo.statusValues.expired', tone: 'danger', symbol: '×' },
    noValidity: { key: 'noValidity', i18nKey: 'priceListInfo.statusValues.noValidity', tone: 'neutral', symbol: '○' }
};

const toDate = (value: Date | string | null | undefined): Date | undefined => {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

/** Vigente / pendiente / vencida / sin vigencia según las fechas de la asociación. */
export const priceListStatus = (
    list: Pick<IAssociatedPriceList, 'effectiveDateStart' | 'effectiveDateEnd'>,
    today: Date = new Date()
): PriceListStatus => {
    const start = toDate(list.effectiveDateStart);
    const end = toDate(list.effectiveDateEnd);
    if (!start && !end) return STATUS.noValidity;
    if (end && today > end) return STATUS.expired;
    if (start && today < start) return STATUS.pending;
    return STATUS.active;
};

/** Días que faltan para el vencimiento (negativo si ya venció); undefined sin fecha de fin. */
export const daysToExpiration = (
    list: Pick<IAssociatedPriceList, 'effectiveDateEnd'>,
    today: Date = new Date()
): number | undefined => {
    const end = toDate(list.effectiveDateEnd);
    if (!end) return undefined;
    const msPerDay = 24 * 60 * 60 * 1000;
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfEnd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((startOfEnd.getTime() - startOfToday.getTime()) / msPerDay);
};

/** La vigencia "desde" no puede ser igual o posterior a la "hasta". */
export const isValidityRangeValid = (start: Date | undefined, end: Date | undefined): boolean =>
    !start || !end || start < end;

export const UNKNOWN_SERVICE = 'Desconocido';

/** Minúsculas y sin acentos para filtrar. */
const fold = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Pone la descripción del servicio (nombre de referencia) en cada precio y aplica el filtro de
 * búsqueda. El orden lo decide después la grilla.
 */
export const enrichPrices = (
    prices: PriceDetails[] | null | undefined,
    services: AvailableMedicalService[] | null | undefined,
    filter: string | undefined
): PriceDetails[] => {
    const byId = new Map<number, string>();
    (services || []).forEach((service) => byId.set(service.id, service.serviceNameReference));
    const query = fold((filter || '').trim());
    return (prices || [])
        .map((price) => ({
            ...price,
            Description: price.itemReferredId ? byId.get(price.itemReferredId) || price.itemReferredName || UNKNOWN_SERVICE : price.Description || ''
        }))
        .filter((price) => !query || fold(price.Description || '').includes(query));
};

/** "1.234,50" con dos decimales, sin símbolo (la moneda va aparte en la celda). */
export const formatPrice = (value: number | null | undefined): string =>
    new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
        typeof value === 'number' && Number.isFinite(value) ? value : 0
    );

/** Texto del input a número; vacío o inválido → undefined. */
export const parsePriceInput = (text: string): number | undefined => {
    const normalized = text.trim().replace(',', '.');
    if (!normalized) return undefined;
    const value = Number(normalized);
    return Number.isFinite(value) && value >= 0 ? value : undefined;
};

/** Cuántos precios quedaron modificados sin guardar (edición masiva). */
export const changedPricesCount = (prices: PriceDetails[] | null | undefined): number =>
    (prices || []).filter((price) => price.isChanged).length;

/** Nombre de lista válido: no vacío, hasta `max` caracteres. */
export const PRICE_LIST_NAME_MAX = 120;
export const validatePriceListName = (name: string | undefined): 'required' | 'tooLong' | undefined => {
    const value = (name || '').trim();
    if (!value) return 'required';
    if (value.length > PRICE_LIST_NAME_MAX) return 'tooLong';
    return undefined;
};
